import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type PrinterMode = 'auto' | 'network';

/**
 * Paper roll width in millimetres. 80mm is by far the most common in modern
 * restaurants (≈ 48 columns at standard 12px font); 58mm is the legacy/portable
 * size (≈ 32 columns). The receipt template (F-4) reads this to pick a layout.
 */
export type PaperWidthMm = 58 | 80;

export const DEFAULT_PAPER_WIDTH_MM: PaperWidthMm = 80;
/** Approximate ESC/POS character columns at the default monospaced font. */
export const COLUMNS_PER_WIDTH: Record<PaperWidthMm, number> = {
  58: 32,
  80: 48,
};

export interface NetworkPrinterConfig {
  host: string;
  port: number;
}

interface PrinterState {
  /** "auto" = enumerate OS-installed printers. "network" = TCP-ping a saved host:port. */
  mode: PrinterMode;
  network: NetworkPrinterConfig | null;
  /**
   * In `auto` mode, the CUPS / OS device name to target (e.g.
   * `PrinterCMD_ESCPO_POS80_Printer_USB`). When `null`, falls back to the
   * OS-reported default printer.
   */
  osPrinterName: string | null;
  /** Master toggle: if false, the badge stays in "no-printer" state and no polling happens. */
  enabled: boolean;
  /** Auto-print every new order onto the configured printer. */
  autoPrint: boolean;
  /** Paper roll width — 80mm is the default for modern restaurants. */
  paperWidthMm: PaperWidthMm;

  setMode: (mode: PrinterMode) => void;
  setNetwork: (cfg: NetworkPrinterConfig | null) => void;
  setOsPrinterName: (name: string | null) => void;
  setEnabled: (enabled: boolean) => void;
  setAutoPrint: (autoPrint: boolean) => void;
  setPaperWidthMm: (paperWidthMm: PaperWidthMm) => void;
}

export const usePrinterStore = create<PrinterState>()(
  persist(
    (set) => ({
      mode: 'auto',
      network: null,
      osPrinterName: null,
      enabled: true,
      autoPrint: true,
      paperWidthMm: DEFAULT_PAPER_WIDTH_MM,

      setMode: (mode) => set({ mode }),
      setNetwork: (network) => set({ network }),
      setOsPrinterName: (osPrinterName) => set({ osPrinterName }),
      setEnabled: (enabled) => set({ enabled }),
      setAutoPrint: (autoPrint) => set({ autoPrint }),
      setPaperWidthMm: (paperWidthMm) => set({ paperWidthMm }),
    }),
    {
      name: 'printer-config',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      migrate: (persisted, version) => {
        // v0 had no paperWidthMm; default to 80mm for existing users.
        const state = (persisted as Partial<PrinterState>) ?? {};
        if (version < 1) {
          return { ...state, paperWidthMm: DEFAULT_PAPER_WIDTH_MM } as PrinterState;
        }
        return state as PrinterState;
      },
    },
  ),
);
